import { useCallback, useState } from 'react';
import { trpc } from '../lib/trpc';

type UseBudgetRegenerateOptions = {
  onSuccess?: () => Promise<unknown> | unknown;
};

export function useBudgetRegenerate(
  options: UseBudgetRegenerateOptions = {},
) {
  const { onSuccess } = options;
  const regenerateMutation = trpc.budget.regenerate.useMutation();
  const [regenerateResult, setRegenerateResult] = useState<string | null>(null);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);

  const clearRegenerateFeedback = useCallback(() => {
    setRegenerateResult(null);
    setRegenerateError(null);
  }, []);

  const runBudgetRegenerate = useCallback(async () => {
    clearRegenerateFeedback();

    try {
      const result = await regenerateMutation.mutateAsync();

      if (result?.success) {
        setRegenerateResult(
          `Built a first-draft budget for ${result.entriesUpdated} categories from your recent spending patterns.`,
        );

        await onSuccess?.();
        return result;
      }

      const message = result?.message ?? 'Could not regenerate budget.';
      setRegenerateError(message);
      return result ?? null;
    } catch {
      setRegenerateError('Could not regenerate budget. Please try again.');
      return null;
    }
  }, [clearRegenerateFeedback, onSuccess, regenerateMutation]);

  return {
    clearRegenerateFeedback,
    isRegenerating: regenerateMutation.isPending,
    regenerateError,
    regenerateResult,
    runBudgetRegenerate,
  };
}
